import { useMemo } from 'react'
import { isCurrentUserAdminOrGlobalAdminSelector } from '@axilon-apps/domains-identity/state'
import {
  type NotificationDigest,
  type NotificationKind,
  type NotificationKindAudience,
  type NotificationPreferences,
  useAppSelector,
  useGetNotificationPreferencesQuery,
  useListNotificationKindAudiencesQuery,
  useResetNotificationKindAudienceMutation,
  useUpdateNotificationKindAudienceMutation,
  useUpdateNotificationPreferencesMutation
} from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Checkbox } from '../../../components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select'
import { Separator } from '../../../components/ui/separator'
import { Switch } from '../../../components/ui/switch'
import { toggleListValue } from './toggle-list'

const DIGEST_OPTIONS: { value: NotificationDigest; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'off', label: 'Off' }
]

function PreferenceRow({
  title,
  hint,
  children
}: {
  title: string
  hint: string
  children: React.ReactNode
}) {
  return (
    <CardContent className="flex items-center justify-between gap-4 px-4 py-2.5">
      <div className="min-w-0">
        <div className="text-sm font-medium text-ink">{title}</div>
        <p className="text-xs text-ink-3">{hint}</p>
      </div>
      {children}
    </CardContent>
  )
}

function PreferencesCard({ activityKinds }: { activityKinds: NotificationKindAudience[] }) {
  const { data: prefs, isLoading } = useGetNotificationPreferencesQuery()
  const [save, { isLoading: saving }] = useUpdateNotificationPreferencesMutation()

  if (isLoading || !prefs) {
    return <div className="px-1 py-4 text-sm text-ink-3">Loading your notification settings…</div>
  }
  const update = (patch: Partial<NotificationPreferences>) => void save({ ...prefs, ...patch })
  const toggle = (key: keyof NotificationPreferences, title: string, hint: string) => (
    <PreferenceRow title={title} hint={hint}>
      <Switch
        checked={Boolean(prefs[key])}
        disabled={saving}
        aria-label={title}
        onCheckedChange={(checked) => update({ [key]: checked })}
      />
    </PreferenceRow>
  )

  return (
    <Card className="gap-0 overflow-hidden py-0 shadow-sm">
      {toggle(
        'show_activity',
        'Show activity in the bell',
        'Completions and receipts appear in an Activity tab. They never count toward the badge.'
      )}
      <Separator />
      {toggle(
        'in_app_toasts',
        'In-app toasts',
        'A short pop-up when something needs you while Prophet is open.'
      )}
      <Separator />
      {toggle(
        'desktop_notifications',
        'Desktop notifications',
        'A system notification when something needs you and Prophet is in the background.'
      )}
      <Separator />
      {toggle(
        'email_needs_you',
        'Email what needs you',
        'Findings, failures, and requests that reach you are also emailed, at most once a day per item.'
      )}
      <Separator />
      <PreferenceRow
        title="Digest email"
        hint="A summary of what is open and what happened, if anything did."
      >
        <Select
          value={prefs.digest}
          onValueChange={(next) => update({ digest: next as NotificationDigest })}
        >
          <SelectTrigger size="sm" aria-label="Digest email" className="shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DIGEST_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PreferenceRow>
      {activityKinds.length > 0 ? (
        <>
          <Separator />
          <CardContent className="flex flex-col gap-2 px-4 py-2.5">
            <div>
              <div className="text-sm font-medium text-ink">Activity to show</div>
              <p className="text-xs text-ink-3">
                Turn off kinds of activity you do not need. What needs you is always shown.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {activityKinds.map((k) => {
                const shown = !prefs.muted_kinds.includes(k.kind)
                return (
                  <label key={k.kind} className="flex items-center gap-2 text-13 text-ink">
                    <Checkbox
                      checked={shown}
                      disabled={saving}
                      onCheckedChange={(checked) =>
                        update({
                          muted_kinds: toggleListValue<NotificationKind>(
                            prefs.muted_kinds,
                            k.kind,
                            checked !== true
                          )
                        })
                      }
                    />
                    {k.label}
                  </label>
                )
              })}
            </div>
          </CardContent>
        </>
      ) : null}
    </Card>
  )
}

function AudienceCard({ kinds, roles }: { kinds: NotificationKindAudience[]; roles: string[] }) {
  const [setAudience, { isLoading: saving }] = useUpdateNotificationKindAudienceMutation()
  const [resetAudience] = useResetNotificationKindAudienceMutation()
  const editable = kinds.filter((k) => !k.targeted_only)

  return (
    <Card className="gap-0 overflow-hidden py-0 shadow-sm">
      <CardContent className="px-4 py-2.5">
        <div className="text-sm font-medium text-ink">Who gets what</div>
        <p className="text-xs text-ink-3">
          Each kind goes to these roles across the organization. A role also reaches everyone above
          it (operator includes engineers and admins). Rules and agents can name people instead.
        </p>
      </CardContent>
      <Separator />
      <div className="overflow-x-auto">
        <table className="w-full text-13">
          <thead>
            <tr className="border-b border-border text-left text-11 text-ink-3">
              <th className="px-4 py-1.5 font-medium">Kind</th>
              {roles.map((role) => (
                <th key={role} className="px-2 py-1.5 text-center font-medium capitalize">
                  {role}
                </th>
              ))}
              <th className="px-4 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {editable.map((k) => (
              <tr key={k.kind} className="border-b border-border last:border-0">
                <td className="px-4 py-1.5 text-ink">
                  <span>{k.label}</span>
                  {k.bucket === 'action' ? (
                    <Badge variant="secondary" className="ml-2 text-10">
                      Needs you
                    </Badge>
                  ) : null}
                  {k.roles.length === 0 ? (
                    <span className="ml-2 text-11 text-ink-3">Everyone</span>
                  ) : null}
                </td>
                {roles.map((role) => (
                  <td key={role} className="px-2 py-1.5 text-center">
                    <Checkbox
                      aria-label={`${k.label}: ${role}`}
                      checked={k.roles.includes(role)}
                      disabled={saving}
                      onCheckedChange={(checked) =>
                        void setAudience({
                          kind: k.kind,
                          roles: toggleListValue(k.roles, role, checked === true)
                        })
                      }
                    />
                  </td>
                ))}
                <td className="px-4 py-1.5 text-right">
                  {k.overridden ? (
                    <Button
                      type="button"
                      size="xs"
                      variant="link"
                      onClick={() => void resetAudience(k.kind)}
                    >
                      Reset
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

/** Settings → Notifications: personal delivery, and (admins) role audiences. */
export function NotificationsSettings() {
  const isAdmin = useAppSelector(isCurrentUserAdminOrGlobalAdminSelector)
  const { data } = useListNotificationKindAudiencesQuery()
  const activityKinds = useMemo(
    () => (data?.kinds ?? []).filter((k) => k.bucket === 'activity'),
    [data]
  )

  return (
    <div className="flex flex-col gap-4">
      <PreferencesCard activityKinds={activityKinds} />
      {isAdmin && data ? <AudienceCard kinds={data.kinds} roles={data.roles} /> : null}
    </div>
  )
}
